import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { STEPS, setSpeed, speed } from "../game/speed";
import { diceSoundOn, soundOn, toggleDiceSound, toggleSound } from "../game/sfx";
import { useSignal } from "../net/signal";
import { Overlay } from "./Overlay";

/**
 * ⚙ ِ میز — سرعت، صدا، صدای تاس و تمام‌صفحه در یک برگه.
 *
 * پیش‌تر هر کدام دکمه‌ی جدایی در سربرگ بود و سرِ میزِ منچ روی گوشی هفت دکمه کنارِ نام می‌نشست
 * (کاربر، ۲۵ اکتبر ۲۰۲۶: «دکمه‌های سربرگ زیاد است»). حالا سربرگ فقط نام، ⚙ و ✕ دارد و چت پایینِ میز
 * است. همتای PauseV3 ِ اپ، که از اول همین چرخ‌دنده را داشت.
 */
export function TableSettings({ sound, dice, onClose }: {
  /** این میز صدا دارد (تخته‌ای‌ها و مافیا) — میزِ ورق صدایی ندارد که خاموش شود. */
  sound: boolean;
  /** بازیِ تاس‌دار — کلیدِ جدای «صدای تاس». */
  dice: boolean;
  onClose: () => void;
}) {
  const k = useSignal(speed);
  const at = Math.max(0, STEPS.findIndex((s) => s.k === k));
  const snd = useSignal(soundOn);
  const dsnd = useSignal(diceSoundOn);
  const fs = useFullscreen();
  return (
    <Overlay label="تنظیمات میز" className="tset" onClose={onClose}>
      <div className="tset-sheet">
        <h2>تنظیمات میز</h2>
        {/* یک نوار و نه چهار دکمه (کاربر، ۲۵ اکتبر ۲۰۲۶): آرام سمتِ راست و سبز، تند سمتِ چپ و سرخ؛ نامِ پله‌ی
            جاری بالای نوار به رنگِ همان پله */}
        <div className="tset-row col" style={{ "--c": SPEED_TINT[at] } as CSSProperties}>
          <span className="tset-head"><span><SpeedIcon />سرعت بازی</span><b>{STEPS[at]!.name}</b></span>
          <div className="tset-speed" style={{ "--f": String(at / (STEPS.length - 1)) } as CSSProperties}>
            <i className="rail" aria-hidden="true" />
            <i className="fill" aria-hidden="true" />
            <i className="ticks" aria-hidden="true">{STEPS.map((s, i) => <i key={s.k} className={i <= at ? "on" : ""} />)}</i>
            <input type="range" min={0} max={STEPS.length - 1} step={1} value={at}
                   onChange={(e) => setSpeed(STEPS[Number(e.target.value)]!.k)}
                   aria-label="سرعت بازی" aria-valuetext={STEPS[at]!.name} />
          </div>
        </div>
        {sound && <Toggle icon={<SoundIcon on={snd} />} label="صدا" on={snd} onClick={toggleSound} />}
        {dice && <Toggle icon={<DiceIcon on={dsnd} />} label="صدای تاس" on={dsnd} onClick={toggleDiceSound} />}
        {fs && <Toggle icon={<FullIcon />} label="تمام‌صفحه" on={fs.on} onClick={fs.toggle} />}
        <button type="button" className="btn tset-done" onClick={onClose}>ادامه‌ی بازی</button>
      </div>
    </Overlay>
  );
}

/** رنگِ هر پله‌ی سرعت، هم‌ترتیب با STEPS — همان رنگ‌های شیبِ نوار (style.css، ‎.tset-speed). */
const SPEED_TINT = ["#34b864", "#a9cc3f", "#f0a830", "#e2493a"];

/** دکمه‌ی ⚙ ِ سربرگ. */
export function GearButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="fs gear" onClick={onClick} title="تنظیمات میز" aria-label="تنظیمات میز" aria-haspopup="dialog">
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
      </svg>
    </button>
  );
}

/** ✕ ِ سربرگ: بیرون از میز یا اتاق (با پرسش، در App)، یا پایانِ تماشا. */
export function CloseButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button className="fs xbtn" onClick={onClick} title={label} aria-label={label}>
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"
           strokeWidth="2.4" strokeLinecap="round">
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    </button>
  );
}

function Toggle({ icon, label, on, onClick }: { icon: ReactNode; label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" className="tset-row" role="switch" aria-checked={on} onClick={onClick}>
      <span>{icon}{label}</span>
      <i className={"tset-sw" + (on ? " on" : "")} aria-hidden="true" />
    </button>
  );
}

/**
 * تمام‌صفحه، اگر این مرورگر بگذارد. روی گوشی نوارِ نشانی ته صفحه را می‌خورد — همان‌جا که دستِ
 * بازیکن است. iOS سافاری این API را برای صفحه‌ی وب ندارد؛ آن‌جا ردیفش نمی‌آید.
 */
export function useFullscreen(): { on: boolean; toggle: () => void } | null {
  // fullscreenEnabled نه وجودِ تابع: قابِ بی‌اجازه و سافاریِ iOS تابع را دارند ولی اجازه نه.
  const can = typeof document !== "undefined" && document.fullscreenEnabled === true;
  const [on, setOn] = useState(() => can && !!document.fullscreenElement);
  useEffect(() => {
    if (!can) return;
    const sync = () => setOn(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, [can]);
  if (!can) return null;
  return {
    on,
    toggle: () => {
      if (document.fullscreenElement) void document.exitFullscreen();
      else document.documentElement.requestFullscreen().catch((e: unknown) => {
        // رد شد (بی‌اشاره‌ی کاربر، یا سیاستِ مرورگر) — بی‌صدا نمی‌گذریم
        console.warn("تمام‌صفحه رد شد:", e);
      });
    },
  };
}

const icon = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true } as const;

function SpeedIcon() {
  return <svg {...icon}><path d="M12 14l4-5" /><path d="M3.5 17a9 9 0 1 1 17 0" /></svg>;
}

function SoundIcon({ on }: { on: boolean }) {
  return (
    <svg {...icon}>
      <path d="M11 5 6 9H3v6h3l5 4V5z" />
      {on ? <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" /> : <path d="m16 9 5 6M21 9l-5 6" />}
    </svg>
  );
}

function DiceIcon({ on }: { on: boolean }) {
  return (
    <svg {...icon}>
      <rect x="4" y="4" width="16" height="16" rx="3.5" />
      <circle cx="9" cy="9" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="15" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
      {!on && <path d="M3 3l18 18" />}
    </svg>
  );
}

function FullIcon() {
  return <svg {...icon}><path d="M3 9V3h6M21 15v6h-6M15 3h6v6M9 21H3v-6" /></svg>;
}
